import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    clearDirectNotificationStatus,
    getDirectNotificationRecipients,
    sendDirectNotification
} from '../../store/actions/directNotificationsActions';
import Spinner from '../_common/Spinner';
import ButtonSpinner from '../_common/ButtonSpinner';
import './DirectNotifications.scss';

const selector = state => {
    const { language, directNotifications } = state;
    return {
        t: language.translator,
        recipientsPending: directNotifications.recipientsPending,
        sendPending: directNotifications.sendPending,
        recipients: directNotifications.recipients,
        result: directNotifications.result,
        recipientsError: directNotifications.recipientsError,
        sendError: directNotifications.sendError
    };
};

const DirectNotifications = () => {
    const {
        t,
        recipientsPending,
        sendPending,
        recipients,
        result,
        recipientsError,
        sendError
    } = useSelector(selector);
    const dispatch = useDispatch();
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [searchText, setSearchText] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);

    useEffect(() => {
        dispatch(clearDirectNotificationStatus());
        dispatch(getDirectNotificationRecipients());
    }, [dispatch]);

    const getRecipientName = recipient => {
        const firstAndLastName = `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim();
        return recipient.fullName || firstAndLastName || recipient.email;
    };

    const matchesSearch = recipient => {
        const normalizedSearchText = searchText.trim().toLowerCase();
        if (!normalizedSearchText) {
            return true;
        }

        const searchValue = [
            recipient.fullName,
            `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim(),
            recipient.email
        ].filter(Boolean).join(' ').toLowerCase();

        return searchValue.includes(normalizedSearchText);
    };

    const filteredRecipients = recipients.filter(matchesSearch);

    const selectAllUsers = () => {
        const filteredRecipientIds = filteredRecipients.map(recipient => recipient.id);
        setSelectedUserIds(currentSelectedUserIds =>
            Array.from(new Set([...currentSelectedUserIds, ...filteredRecipientIds]))
        );
        setValidationErrors([]);
        dispatch(clearDirectNotificationStatus());
    };

    const deselectAllUsers = () => {
        setSelectedUserIds([]);
        dispatch(clearDirectNotificationStatus());
    };

    const toggleUser = userId => {
        setSelectedUserIds(currentSelectedUserIds =>
            currentSelectedUserIds.includes(userId)
                ? currentSelectedUserIds.filter(selectedUserId => selectedUserId !== userId)
                : [...currentSelectedUserIds, userId]
        );
        setValidationErrors([]);
        dispatch(clearDirectNotificationStatus());
    };

    const validate = () => {
        const errors = [];

        if (!title.trim()) {
            errors.push(t.gettext('Notification title is required'));
        }
        if (!message.trim()) {
            errors.push(t.gettext('Notification text is required'));
        }
        if (!selectedUserIds.length) {
            errors.push(t.gettext('Please select at least one user'));
        }

        return errors;
    };

    const send = event => {
        event.preventDefault();
        dispatch(clearDirectNotificationStatus());

        const errors = validate();
        setValidationErrors(errors);
        if (errors.length) {
            return;
        }

        dispatch(sendDirectNotification({
            title: title.trim(),
            message: message.trim(),
            recipientUserIds: selectedUserIds
        }));
    };

    if (recipientsPending) {
        return (
            <div className='page'>
                <Spinner size={300}/>
            </div>
        );
    }

    return (
        <div className='directNotifications'>
            <div className='directNotificationsHeader'>
                {t.gettext('Direct notifications sending')}
            </div>
            <div className='directNotificationsContent'>
                <div className='directNotificationsSection directNotificationsSection--users'>
                    <div className='directNotificationsSectionHeader'>
                        <span>{t.gettext('Users')}</span>
                        <span className='directNotificationsSelectedCounter'>
                            {selectedUserIds.length}/{recipients.length}
                        </span>
                    </div>
                    <div className='directNotificationsToolbar'>
                        <button
                            className='directNotificationsSecondaryButton'
                            onClick={selectAllUsers}
                            disabled={!filteredRecipients.length}
                        >
                            {t.gettext('Select all')}
                        </button>
                        <button
                            className='directNotificationsSecondaryButton'
                            onClick={deselectAllUsers}
                            disabled={!selectedUserIds.length}
                        >
                            {t.gettext('Deselect all')}
                        </button>
                        <div className='directNotificationsSearch'>
                            <span className='material-icons directNotificationsSearchIcon'>
                                search
                            </span>
                            <input
                                value={searchText}
                                onChange={event => setSearchText(event.target.value)}
                                placeholder={t.gettext('Search user')}
                            />
                        </div>
                    </div>
                    {recipientsError &&
                        <div className='directNotificationsMessage directNotificationsMessage--error'>
                            {t.gettext('Could not load users')}
                        </div>
                    }
                    {!recipientsError && !filteredRecipients.length &&
                        <div className='directNotificationsEmpty'>{t.gettext('No users')}</div>
                    }
                    <div className='directNotificationsUsersList'>
                        {filteredRecipients.map(recipient => (
                            <label
                                key={recipient.id}
                                className='directNotificationsUser'
                            >
                                <input
                                    type='checkbox'
                                    checked={selectedUserIds.includes(recipient.id)}
                                    onChange={() => toggleUser(recipient.id)}
                                />
                                <span className='directNotificationsUserText'>
                                    <span className='directNotificationsUserName'>
                                        {getRecipientName(recipient)}
                                    </span>
                                    {recipient.email &&
                                        <span className='directNotificationsUserEmail'>
                                            {recipient.email}
                                        </span>
                                    }
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
                <form className='directNotificationsSection directNotificationsSection--form' onSubmit={send}>
                    <label className='directNotificationsField'>
                        <span>{t.gettext('Notification title')}</span>
                        <input
                            value={title}
                            onChange={event => setTitle(event.target.value)}
                            disabled={sendPending}
                        />
                    </label>
                    <label className='directNotificationsField directNotificationsField--message'>
                        <span>{t.gettext('Notification text')}</span>
                        <textarea
                            value={message}
                            onChange={event => setMessage(event.target.value)}
                            disabled={sendPending}
                        />
                    </label>
                    {!!validationErrors.length &&
                        <div className='directNotificationsMessage directNotificationsMessage--error'>
                            {validationErrors.map(error => (
                                <div key={error}>{error}</div>
                            ))}
                        </div>
                    }
                    {sendError &&
                        <div className='directNotificationsMessage directNotificationsMessage--error'>
                            {t.gettext('Could not send notification')}
                        </div>
                    }
                    {result &&
                        <div className='directNotificationsMessage directNotificationsMessage--success'>
                            <div>{t.gettext('Notification sent')}</div>
                            <div>
                                {t.gettext('Sent')}: {result.sent}. {t.gettext('Skipped')}: {result.skipped}.
                            </div>
                            {!!result.invalidUserIds.length &&
                                <div>
                                    {t.gettext('Invalid users')}: {result.invalidUserIds.join(', ')}
                                </div>
                            }
                        </div>
                    }
                    <button
                        className='directNotificationsSendButton'
                        type='submit'
                        disabled={sendPending}
                    >
                        {t.gettext('Send notification')}
                        {sendPending && <ButtonSpinner size={18}/>}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default DirectNotifications;
